'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Feedback';
import { Icon } from '@/components/ui/Icon';
import { useLeadSubmission } from '@/hooks/useLeadSubmission';
import { hasErrors, isBlank, isEmail, messages, type ValidationErrors } from '@/lib/validation';

import { SubmissionError, SubmissionSuccess } from './FormFeedback';

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  business: string;
  topic: string;
  message: string;
}

const TOPICS = [
  { value: 'tablet-app', label: 'The tablet app version' },
  { value: 'cabinets', label: 'Metal cabinets (21.5" / 43")' },
  { value: 'affiliate-code', label: 'Affiliate codes' },
  { value: 'platform', label: 'Listing my platform' },
  { value: 'support', label: 'Support for an existing device' },
  { value: 'other', label: 'Something else' },
];

function validate(values: ContactFormValues): ValidationErrors<ContactFormValues> {
  return {
    name: isBlank(values.name) ? messages.required : undefined,
    email: isBlank(values.email) ? messages.required : isEmail(values.email) ? undefined : messages.email,
    topic: isBlank(values.topic) ? messages.required : undefined,
    message: isBlank(values.message) ? messages.required : undefined,
  };
}

export function ContactForm({ defaultTopic = '' }: { defaultTopic?: string }) {
  const [values, setValues] = useState<ContactFormValues>({
    name: '',
    email: '',
    phone: '',
    business: '',
    topic: defaultTopic,
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors<ContactFormValues>>({});
  const submission = useLeadSubmission();

  const setValue = <K extends keyof ContactFormValues>(key: K, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;

    await submission.submit({
      type: 'contact',
      name: values.name,
      email: values.email,
      phone: values.phone,
      business: values.business,
      message: values.message,
      details: { topic: TOPICS.find((topic) => topic.value === values.topic)?.label ?? values.topic },
    });
  };

  if (submission.status === 'success') {
    return (
      <SubmissionSuccess
        title="Message sent"
        description="Thanks for getting in touch. We read every message and normally reply within one business day."
        onReset={submission.reset}
        resetLabel="Send another message"
      />
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <TextField
            label="Your name"
            required
            autoComplete="name"
            value={values.name}
            error={errors.name}
            onChange={(event) => setValue('name', event.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={values.email}
            error={errors.email}
            onChange={(event) => setValue('email', event.target.value)}
          />
          <TextField
            label="Phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={values.phone}
            onChange={(event) => setValue('phone', event.target.value)}
          />
          <TextField
            label="Business name"
            autoComplete="organization"
            value={values.business}
            onChange={(event) => setValue('business', event.target.value)}
          />
        </div>

        <SelectField
          label="What is this about?"
          required
          placeholder="Choose a topic"
          options={TOPICS}
          value={values.topic}
          error={errors.topic}
          onChange={(event) => setValue('topic', event.target.value)}
        />

        <TextAreaField
          label="Message"
          required
          rows={6}
          value={values.message}
          error={errors.message}
          onChange={(event) => setValue('message', event.target.value)}
        />

        <SubmissionError submission={submission} />

        <Button type="submit" size="lg" disabled={submission.status === 'submitting'} className="self-start">
          {submission.status === 'submitting' ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            <>
              Send message
              <Icon name="arrow" className="h-5 w-5" />
            </>
          )}
        </Button>
      </form>
    </Card>
  );
}
